import { ApplicationElement, html } from '../application_element/element.js';
import CommonActions from '../common_actions/element.js';
import translations from './translations.js';
import style from './style.lit.css';

/**
 * Base component for wrappers like: OrganizeOptionWrapper, DateOptionWrapper, PollOptionWrapper
 *
 * @prop totalCount - Total number of distinct users that have any option checked. It will be updated by component when user checks/unchecks.
 * @prop resultsUrl - When provided wraps 'n members assigned/voted' label to a link (optional)
 * @prop clearVotesUrl - API url that will remove all current user's votes on this card. Expected endpoint method: `delete`.
 * @prop isClosed - When true, disables all options and renders closed voting message (optional)
 * @prop isMultipleChoice - Internal property - will be set automatically - can 2 options be selected at the same time?
 *
 * @cssprop [--options-padding: 16px 0 16px 0] Padding of each slotted option
 * @cssprop [--options-border-bottom: 1px solid var(--border-light)] Bottom border of each slotted option
 * @cssprop [--notes-padding: 16px 0 16px 0] Padding of notes section
 * @cssprop [--notes-color: var(--dark-text-secondary)] Color of notes section text
 * @cssprop [--notes-font-size: var(--font-size-sm, 12px)] Size of notes section text
 * @cssprop [--notes-font-style: italic] Style of notes section text
 * @cssprop [--notes-gap: 4px] Gap between notes in notes section
 * @cssprop [--checkbox-button-border-radius: 4px] Border radius of multi-choice option buttons.
 * @cssprop [--radio-button-border-radius: 50%] Border radius of single-choice option buttons.
 *
 * @slot default - For card options
 * @slot clear - For clear votes action in footer
 * @slot actions - For additional actions in footer
 */

export default class CardOptionWrapper extends ApplicationElement {
  static styles = [ApplicationElement.styles, style];

  static properties = {
    resultsUrl: { type: String },
    clearVotesUrl: { type: String },
    isClosed: { type: Boolean },
    totalCount: { type: Number },
    isMultipleChoice: { type: Boolean, reflect: true },
  };

  /** @internal */
  static translations = translations;

  constructor() {
    super();
    this.totalCount = 0;

    /** @internal */
    this.commonActions = new CommonActions(this);
  }

  /** @internal */
  get options() {
    // implement options getter
    return Array.from(this.querySelectorAll('ef-card-option'));
  }

  /** @internal */
  get checkedCount() {
    return this.options.filter(o => o.checked).length;
  }

  /** @internal */
  get hideClearButton() {
    return this.isClosed || !this.checkedCount || !this.clearVotesUrl;
  }

  handleSlotChange() {
    let uniqueCount = 0;

    this.options.forEach(o => {
      o.disabled ||= this.isClosed;
      o.AppConfig = this.AppConfig;
      o.unique && uniqueCount++;
    });

    // if we have 2+ non-unique options - it is a multipleChoice
    this.isMultipleChoice = this.options.length - uniqueCount > 1;
  }

  handleInput(e) {
    const { target } = e;
    const checked = target.checked;

    if (checked && this.checkedCount === 1) this.totalCount++;
    else if (!checked && this.checkedCount === 0) this.totalCount--;

    if (checked) this.handleRadioOptions(target);
  }

  handleRadioOptions(option) {
    if (option.unique)
      // uncheck all other options
      this.options.forEach(o => o.itemId !== option.itemId && o.calcUncheck());
    // uncheck all other, unique options
    else this.options.forEach(o => o.unique && o.calcUncheck());
  }

  async handleClearVotes() {
    try {
      await this.commonActions.delete(this.clearVotesUrl);
      this.options.forEach(o => {
        if (o.checked) {
          o.calcUncheck();
          o.dispatchChangeEvent();
        }
      });
    } catch {
      this.commonActions.displaySnackBarError(
        this.t('snack.generic_error_msg')
      );
    }
  }

  renderTotalLabel() {
    const label =
      this.isClosed && !this.totalCount
        ? this.t('no_total')
        : this.t('total', { count: this.totalCount });

    return this.resultsUrl
      ? html`<a href=${this.resultsUrl}>${label}</a>`
      : label;
  }

  renderClosedLabel() {
    return html`<div>${this.t('closed')}</div>`;
  }

  render() {
    return html`
      <slot
        @cardOption-change=${this.handleInput}
        @slotchange=${this.handleSlotChange}>
      </slot>
      <div class="note">
        <div>
          ${this.renderTotalLabel()}
          <div>
            <slot
              name="clear"
              ?hidden=${this.hideClearButton}
              @click=${this.handleClearVotes}>
              <button class="clear">${this.t('clear_votes')}</button>
            </slot>
            <slot name="actions"></slot>
          </div>
        </div>
        ${this.isClosed ? this.renderClosedLabel() : null}
      </div>
    `;
  }
}

CardOptionWrapper.registerTagName('ef-card-option-wrapper');

customElements.get('ef-card-option-wrapper') ||
  customElements.define('ef-card-option-wrapper', CardOptionWrapper);
